import React, { useState } from 'react';
import { Dialog, DialogTitle, IconButton, DialogContent, Button, Typography, TextField, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { useDestinationStore } from 'Application/store';
import { ThemeMode } from 'config';

interface AddDestination {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const AddDestination: React.FC<AddDestination> = ({ isOpen, setIsOpen }) => {
    const { addDestination, isLoading } = useDestinationStore();

    const [formData, setFormData] = useState({
        destinations: [] as string[] // Changed to array of strings
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFormData({
            destinations: value.split(',').map((destination) => destination.trim())
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            await addDestination(formData); // formData contains the array of destinations
            toast.success('Destination added successfully');
            setIsOpen(false);
            setFormData({
                destinations: [] // Reset to an empty array
            });
        } catch (error) {
            toast.error(`Failed to create destination: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Create a new Destination</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Destination Name(s)
                        </Typography>
                        <TextField
                            placeholder="Enter Destination / Country Name(s), separated by commas"
                            required
                            name="destinations"
                            variant="outlined"
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddDestination;
